// ── 연차 관리 파생 데이터 훅 (useMemo 집합) ────────────────────────────────────
import { useMemo } from 'react';
import {
  PAGE_SIZE, TYPE_TO_LOCKED_CATEGORY, TYPE_WITH_PAID_UNPAID_ONLY,
  PAID_UNPAID_CATEGORIES, LEAVE_CATEGORIES, HALF_LEAVE_TYPES, BULK_COLUMNS,
} from './constants';
import {
  isBulkRowEmpty, getBulkCellError, getHalfLeaveThreshold, getWeekdayKeyFromYmd,
  matchesLeaveFilter, getAttendanceSettingsForUser,
} from './utils';

/**
 * useLeaveManagement 훅이 반환한 상태 객체를 받아 useMemo 파생값을 반환합니다.
 * @param {object} state - useLeaveManagement 반환값
 */
export function useLeaveDerived(state) {
  const {
    list, nameQuery, currentPage, activeLeaveFilters,
    users, selectedUserIds, attendanceSettings, attendanceSettingsByCompany,
    form, bulkRows, userMap,
  } = state;

  // ── 검색 + 타입 필터링 (이름 AND 타입 필터 배열 OR) ───────────────────────
  const filteredList = useMemo(() => {
    const keyword = String(nameQuery || '').trim();
    const typeFilters = Array.isArray(activeLeaveFilters) ? activeLeaveFilters : [];
    return list.filter((row) => {
      if (keyword && !String(row.name || '').includes(keyword)) return false;
      if (typeFilters.length > 0 && !typeFilters.some((k) => matchesLeaveFilter(row, k))) return false;
      return true;
    });
  }, [list, nameQuery, activeLeaveFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));

  const pagedList = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, currentPage]);

  // ── 구분 잠금 여부 및 선택 가능한 구분 목록 ──────────────────────────────
  const lockedCategory = TYPE_TO_LOCKED_CATEGORY[form.type] || null;
  const categoryOptions = useMemo(() => {
    if (lockedCategory) return [lockedCategory];
    if (TYPE_WITH_PAID_UNPAID_ONLY.has(form.type)) return PAID_UNPAID_CATEGORIES;
    return LEAVE_CATEGORIES;
  }, [lockedCategory, form.type]);

  // ── 직원 선택 그리드 목록 (재직중 or 전체) ───────────────────────────────
  const pickerUsers = useMemo(
    // is_active=true 이고 resign_date 없는 직원만 재직중으로 판단
    () => state.showResignedInPicker ? users : users.filter((u) => u.is_active && !u.resign_date),
    [users, state.showResignedInPicker]
  );

  // ── 반차기준시간 미설정 경고 (복수 직원 신규 추가 폼용) ───────────────────
  const multiUserHalfLeaveWarning = useMemo(() => {
    if (!HALF_LEAVE_TYPES.has(form.type)) return null;
    if (selectedUserIds.length === 0) return null;
    const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const missingTypes = new Set();

    for (const uid of selectedUserIds) {
      const u = users.find((x) => String(x.id) === String(uid));
      if (!u?.attendance_time_type) continue;
      const companyCode = String(u.company_code || 'PINE').trim().toUpperCase() || 'PINE';
      const userSettings = getAttendanceSettingsForUser(attendanceSettingsByCompany, attendanceSettings, u);
      const templates = userSettings?.templates || [];
      if (templates.length === 0) continue;
      const tKey = String(u.attendance_time_type).trim().toLowerCase();
      const template = templates.find(
        (t) => String(t.name || '').trim().toLowerCase() === tKey
          || String(t.id || '').trim().toLowerCase() === tKey
      ) || templates[0];
      // 근무일 중 threshold 미설정 요일이 하나라도 있으면 해당 유형을 경고 대상으로
      const hasMissing = weekdayKeys.some((wk) => {
        const rule = template?.weekly_rules?.[wk];
        return rule?.is_workday && !rule?.half_leave_threshold;
      });
      if (hasMissing) missingTypes.add(`${companyCode} / ${u.attendance_time_type}`);
    }

    if (missingTypes.size === 0) return null;
    return `'${[...missingTypes].join(', ')}' 근무유형의 일부 요일에 반차기준시간이 설정되지 않았습니다. 출퇴근 관리 > 출퇴근 시간 관리에서 설정해 주세요.`;
  }, [form.type, selectedUserIds, attendanceSettings, attendanceSettingsByCompany, users]);

  // ── 반차기준시간 미설정 경고 (단건 폼용) ──────────────────────────────────
  const halfLeaveThresholdWarning = useMemo(() => {
    if (!HALF_LEAVE_TYPES.has(form.type)) return null;
    const hasAnySettings = Boolean(attendanceSettings) || Object.keys(attendanceSettingsByCompany || {}).length > 0;
    const templates = hasAnySettings ? [{}] : [];
    if (templates.length === 0) return null; // 설정 미로드 시 경고 생략

    const userId = String(form.user_id || '');
    if (!userId || !form.date) return null;

    const selectedUser = users.find((u) => String(u.id) === userId);
    if (!selectedUser?.attendance_time_type) return null;
    const companyCode = String(selectedUser.company_code || 'PINE').trim().toUpperCase() || 'PINE';
    const userSettings = getAttendanceSettingsForUser(attendanceSettingsByCompany, attendanceSettings, selectedUser);
    const userTemplates = userSettings?.templates || [];
    if (userTemplates.length === 0) return null;

    const threshold = getHalfLeaveThreshold(userTemplates, selectedUser.attendance_time_type, form.date);
    if (threshold) return null; // 설정됨 → 경고 없음

    const weekdayNames = { sun: '일', mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토' };
    const wKey = getWeekdayKeyFromYmd(form.date);
    const dayLabel = wKey ? `(${weekdayNames[wKey]})` : '';
    return `'${companyCode} / ${selectedUser.attendance_time_type}' 근무유형의 ${form.date}${dayLabel} 반차기준시간이 설정되지 않았습니다. 출퇴근 관리 > 출퇴근 시간 관리에서 먼저 설정해 주세요.`;
  }, [form.type, form.user_id, form.date, attendanceSettings, attendanceSettingsByCompany, users]);

  // ── 대량 붙여넣기 검증 결과 ───────────────────────────────────────────────
  const bulkErrors = useMemo(
    () => bulkRows.map((row) =>
      Object.fromEntries(BULK_COLUMNS.map((col) => [col.key, getBulkCellError(row, col.key, userMap)]))
    ),
    [bulkRows, userMap]
  );

  const bulkNonEmptyCount = useMemo(
    () => bulkRows.filter((r) => !isBulkRowEmpty(r)).length,
    [bulkRows]
  );

  const bulkInvalidRowCount = useMemo(
    () => bulkRows.filter((row, i) =>
      !isBulkRowEmpty(row) && BULK_COLUMNS.some((col) => Boolean(bulkErrors[i][col.key]))
    ).length,
    [bulkRows, bulkErrors]
  );

  return {
    filteredList, totalPages, pagedList,
    lockedCategory, categoryOptions, pickerUsers,
    multiUserHalfLeaveWarning, halfLeaveThresholdWarning,
    bulkErrors, bulkNonEmptyCount, bulkInvalidRowCount,
  };
}
